/**
 * Steg 2 i sangimporten (bare ved behov): fyll inn artist (påkrevd) og toneart (valgfri) for sanger som mangler det.
 * Ren visning: svarene sendes opp til veiviseren, som lager CSV.
 */
import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { Sang, SangSvar } from '../../common/import/sangfiler/lesSangfiler';
import { TONEARTER } from './toneartliste';


interface Steg2ManglerProps {
  sanger: Sang[];
  /** Svarene eies av veiviseren, så de overlever Tilbake fra kontrollen. */
  svar: SangSvar;
  onSvarEndret: (svar: SangSvar) => void;
  /** Forhåndsvisning pågår: knappene er deaktivert. */
  opptatt?: boolean;
  onFortsett: (svar: SangSvar) => void;
  onHoppOver: () => void;
  onTilbake: () => void;
  /** Elementet stegtittelen vises i; veiviseren sender dialogens DialogTitle. */
  Tittel?: React.ElementType;
}

const tittelFor = (sang: Sang): string => sang.felter.tittel?.trim() || sang.filnavn;

const lagOverskrift = (sanger: Sang[]): string => {
  const toneart = sanger.some((s) => s.mangler.toneart);
  const artist = sanger.some((s) => s.mangler.artist);
  const hva = toneart && artist ? 'toneart eller artist' : toneart ? 'toneart' : 'artist';
  return `${sanger.length} ${sanger.length === 1 ? 'sang' : 'sanger'} trenger ${hva}`;
};

const Steg2Mangler: React.FC<Steg2ManglerProps> = ({ sanger, svar, onSvarEndret, opptatt = false, onFortsett, onHoppOver, onTilbake, Tittel = 'h2' }) => {
  const vist = sanger.filter((s) => s.mangler.toneart || s.mangler.artist);
  const [artistMangler, setArtistMangler] = useState<Set<string>>(new Set());
  // Artist er påkrevd av serveren: «Hopp over» finnes bare når ingen sang mangler artist.
  const kanHoppeOver = !vist.some((s) => s.mangler.artist);

  const sett = (filnavn: string, felt: 'toneart' | 'artist', verdi: string) => {
    onSvarEndret({ ...svar, [filnavn]: { ...svar[filnavn], [felt]: verdi } });
    if (felt === 'artist' && verdi.trim()) {
      setArtistMangler((forrige) => {
        const neste = new Set(forrige);
        neste.delete(filnavn);
        return neste;
      });
    }
  };

  const handleFortsett = () => {
    const tomme = vist.filter((s) => s.mangler.artist && !svar[s.filnavn]?.artist?.trim()).map((s) => s.filnavn);
    if (tomme.length > 0) {
      setArtistMangler(new Set(tomme));
      return;
    }
    const ut: SangSvar = {};
    for (const s of vist) {
      const toneart = s.mangler.toneart ? svar[s.filnavn]?.toneart?.trim() : '';
      const artist = s.mangler.artist ? svar[s.filnavn]?.artist?.trim() : '';
      if (toneart || artist) ut[s.filnavn] = { ...(toneart ? { toneart } : {}), ...(artist ? { artist } : {}) };
    }
    onFortsett(ut);
  };

  return (
    <div className="min-w-0 space-y-4">
      <div className="space-y-1">
        <p className="text-sm text-muted-foreground">Steg 2 av 3</p>
        <Tittel
          tabIndex={-1}
          data-steg-tittel=""
          className="text-lg font-semibold leading-none tracking-tight focus:outline-none"
        >
          {lagOverskrift(vist)}
        </Tittel>
      </div>

      <ul className="space-y-3">
        {vist.map((s, i) => {
          const tittel = tittelFor(s);
          const id = `steg2-${i}`;
          return (
            <li key={s.filnavn} data-testid="steg2-rad" className="min-w-0 space-y-2 rounded-md border p-3">
              <p className="truncate text-sm font-medium">{tittel}</p>
              {s.mangler.toneart && (
                <div className="space-y-1">
                  <p className="text-sm">Toneart (valgfri)</p>
                  <Label htmlFor={`${id}-toneart`} className="sr-only">{`Toneart for ${tittel}`}</Label>
                  <select
                    id={`${id}-toneart`}
                    value={svar[s.filnavn]?.toneart ?? ''}
                    onChange={(e) => sett(s.filnavn, 'toneart', e.target.value)}
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:w-48"
                  >
                    <option value="">Velg toneart</option>
                    {TONEARTER.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <p className="text-sm text-muted-foreground">Trengs for å vise tallene som bokstaver</p>
                </div>
              )}
              {s.mangler.artist && (
                <div>
                  <Label htmlFor={`${id}-artist`} className="sr-only">{`Artist for ${tittel}`}</Label>
                  <Input
                    id={`${id}-artist`}
                    placeholder="Artist"
                    value={svar[s.filnavn]?.artist ?? ''}
                    onChange={(e) => sett(s.filnavn, 'artist', e.target.value)}
                    aria-invalid={artistMangler.has(s.filnavn) || undefined}
                    aria-describedby={artistMangler.has(s.filnavn) ? `${id}-artist-feil` : undefined}
                  />
                  {artistMangler.has(s.filnavn) && (
                    <p id={`${id}-artist-feil`} role="alert" className="mt-1 text-sm text-destructive">
                      Fyll inn artist
                    </p>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button type="button" variant="outline" disabled={opptatt} onClick={onTilbake}>
          Tilbake
        </Button>
        <div className="flex flex-wrap gap-2">
          {kanHoppeOver && (
            <Button type="button" variant="ghost" disabled={opptatt} onClick={onHoppOver}>
              Hopp over
            </Button>
          )}
          <Button type="button" disabled={opptatt} onClick={handleFortsett}>
            {opptatt && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            Fortsett
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Steg2Mangler;
